var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defaultSocketOptions, } from './framework/CommandSocket';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class I3TouchEx {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.deviceId = '01';
        this.isMacAddressSpecified = false;
        this.standByEnabled = false;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}G0000`, /\:\d+r000(\d)/i, tags);
                this.standByEnabled = match[1] === '0';
                // If standby is not enabled, the device returns '1' when powered on and '0' when
                // powered off. If standby is enabled, the device returns '3' when powered on and '2'
                // when powered off.
                return match[1] === '1' || match[1] === '3';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Clear any pending busy timeouts that would set busy back to false. The power set
                // methods will set busy to true and manage the transition back to false.
                this.busyTimeout.clear();
                if (this.isMacAddressSpecified) {
                    if (this.socket.options.allowNoConnection) {
                        return this.setPowerWakeOnLanNetwork(power, tags);
                    }
                    else {
                        return this.setPowerWakeOnLanSerial(power, tags);
                    }
                }
                return this.setPowerSimple(power, tags);
            }),
            powerOffRestrictions: (config) => {
                return config.macAddress === undefined ? [] : ['get'];
            },
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}G:000`, /\:\d+r\:(\d+)/i, tags);
                return parseInt(match[1], 10).toString(); // remove leading zeros
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}S:${code.padStart(3, '0')}`, /^\d+(\+|-)/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return match[1] === '+' ? code : this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}G8000`, /\:\d+r8(\d+)/i, tags);
                return parseInt(match[1], 10);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}S8${volume.toString().padStart(3, '0')}`, /^\d+(\+|-)/i, tags);
                return match[1] === '+' ? volume : this.state.volume.value;
            }),
            range: { max: 100, min: 0, round: 1 },
            powerOffRestrictions: ['get', 'set'],
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}G9000`, /\:\d+r900(\d)/i, tags);
                return match[1] === '1';
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`:${this.deviceId}S900${mute ? '1' : '0'}`, /^\d+(\+|-)/i, tags);
                return match[1] === '+' ? mute : this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 3000, controls: ['power', 'videoInput'] },
            {
                intervalMs: 5000,
                pollWhenDisconnected: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.state.power.value &&
                        this.socket.options.allowNoConnection &&
                        this.socket.getState() !== 'connected') {
                        if (!this.framework.isReconnecting)
                            yield this.framework.reconnect();
                    }
                }),
            },
        ];
        if (this.socket.options.allowNoConnection) {
            // If the display is powered off externally (e.g., IR remote or third party controller),
            // then we will lose the socket connection. If this happens, we update the power state
            // to false.
            this.socket.onConnectionLost(() => {
                this.state.power.update(false, true);
            });
        }
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.isMacAddressSpecified = config.macAddress !== undefined;
            if (this.isMacAddressSpecified && config.port === undefined) {
                this.log.error(`mac address configured for wake on lan, but no port specified`);
            }
            if (config.macAddress !== undefined) {
                // The mac address is specified, so we can't query the power if the device is powered
                // off. The power is initialized to false, but we need to query it if we can when we
                // first connect.
                if (this.socket.port !== 4664 || this.socket.state === 'connected') {
                    this.state.power.update(yield this.power.get(['power.get']));
                }
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.socket.state === 'connected') {
                const result = yield CommandSocketInitializer.initialize(this.socket, [
                    {
                        errorCode: 'verifyFailed',
                        send: `:${this.deviceId}G0000`,
                        next: /\d+r\d+/i,
                    },
                ], credentials);
                return result;
            }
            else if (this.socket.options.allowNoConnection) {
                return 'connected';
            }
            return 'connectFailed';
        });
    }
    // other class methods
    setPowerSimple(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.state.busy.update(true);
            this.state.power.update(power);
            try {
                this.framework.suspendPolling(10000);
                const match = yield this.socket.send(`:${this.deviceId}S000${power ? (this.standByEnabled ? '1' : '3') : '2'}`, /^\d+(\+|-)/i, tags);
                if (match[1] === '+') {
                    // It takes a lot longer to power on than to come out of standby.
                    const powerOnBusyMs = this.standByEnabled ? 4000 : 40000;
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), power ? powerOnBusyMs : 6000);
                    this.framework.suspend(power ? powerOnBusyMs : 6000);
                    this.state.power.update(power);
                }
            }
            catch (_a) { }
            return this.state.power.value;
        });
    }
    setPowerWakeOnLanNetwork(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(15000);
            this.socket.removeCommands('poll');
            if (power) {
                // For direct Ethernet connections where the socket connection is lost when the display
                // is powered off, we first uppdate the power state so the UI will reflect that the
                // power is on, then we block while sending a wake on lan request and attempting to
                // reconnect to the display.
                // If the socket is connected, send a power on command in case wake on lan is not
                // required.
                if (this.socket.state === 'connected') {
                    yield this.socket.sendAsync(`:${this.deviceId}S000${this.standByEnabled ? '1' : '3'}`, tags);
                }
                // For direct Ethernet connections where the socket connection is lost when the display
                // is powered off, we first uppdate the power state so the UI will reflect that the
                // power is on, then we block while sending a Wake on LAN request and attempting to
                // reconnect to the display.
                this.state.busy.update(true);
                // Update power with a short delay so that framework.reconnect gets called first. This
                // tells StateBridge we are disconnected so it doesn't change phase back to configuring
                // to query the controls with power off restrictions when it sees that the power is
                // back on.
                this.timeouts.set(() => this.state.power.update(power), 100);
                yield this.framework.reconnect({
                    attemptDelayMs: 1000,
                    initialDelayMs: 0,
                    numAttempts: 8,
                    onBeforeReconnect: () => __awaiter(this, void 0, void 0, function* () {
                        return this.framework.sendWakeOnLan();
                    }),
                });
                // When updating the busy state, we must use notifyIfUnchanged = true. This/ is because
                // the driver reconnects as part of powering on, and reconnecting resets all the states.
                // Without notifyIfUnchanged = true, the busy = false notification won't get sent.
                this.state.busy.update(false, true);
                return this.socket.state === 'connected';
            }
            else {
                return this.setPowerSimple(power, tags);
            }
        });
    }
    setPowerWakeOnLanSerial(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(6000);
            this.socket.removeCommands('poll');
            if (power) {
                // For serial connections where the display is unresponsive when powered off, first we
                // update the power state so the UI will reflect that the power is on, then we block
                // while sending a wake on lan request, then we continue blocking while sending some
                // power requests to confirm that the power is on, finally we return the new power
                // state.
                this.state.busy.update(true);
                this.state.power.update(true);
                let newPower = false;
                for (let i = 0; i < 4; i++) {
                    this.framework.suspendPolling(6000);
                    yield this.framework.sendWakeOnLan();
                    try {
                        const match = yield this.socket.send(`:${this.deviceId}G0000`, /\:\d+r000(\d)/i, ['power.get']);
                        this.standByEnabled = match[1] === '0';
                        // If standby is not enabled, the device returns '1' when powered on and '0' when
                        // powered off. If standby is enabled, the device returns '3' when powered on and '2'
                        // when powered off.
                        newPower = match[1] === '1' || match[1] === '3';
                        break;
                    }
                    catch (_a) { }
                }
                this.state.busy.update(false);
                return newPower;
            }
            else {
                return this.setPowerSimple(power, tags);
            }
        });
    }
}
// static properties
I3TouchEx.driverType = 'socketDriver';
I3TouchEx.key = '-MWiq0w_HGu3bSIdTY7w';
I3TouchEx.socketOptions = (config) => {
    const baseSocketOptions = {
        commandExtractor: {
            terminators: ['\r'],
        },
        sendTerminator: '\r',
    };
    const hasMacAddress = config.macAddress !== undefined;
    // If no port is defined, then this is the EX series which only supports serial
    // communications using port 4999 through a Global Caché.
    if (config.port === undefined) {
        return [
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4999, idleTimeoutMs: hasMacAddress ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: false }),
        ];
    }
    // Assume port 4664 is direct Ethernet to the device and anything else is via an Ethernet
    // to serial converter.
    return [
        Object.assign(Object.assign({}, baseSocketOptions), { port: config.port, idleTimeoutMs: hasMacAddress && config.port !== 4664 ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: hasMacAddress && config.port === 4664 }),
    ];
};
